import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import productService from "../../services/productService";
import categoryService from "../../services/categoryService";
import subcategoryService from "../../services/subcategoryService";
import laboratoireService from "../../services/laboratoireService";
import ProductDetailModal from "./ProductDetailModal";
import { TableSkeleton } from "../../components/Skeleton";
import { useAppearance } from "../../context/AppearanceContext";
import { IcPlus, IcPencil, IcSearch, IcRefresh, IcClose, IcChevronLeft, IcChevronRight, IcPackageOpen, IcEye, IcTrash, IcFilters, IcPercent } from '../../theme/icons';

const PAGE_SIZE = 10;

const ProductList = () => {
  const { text: appText } = useAppearance();
  const navigate = useNavigate();
  // Selected product for the detail popup (null = closed).
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Last known first page painted at once; the fetch below refreshes it.
  const [products, setProducts] = useState(() => productService.peekProductsPage({ page: 1, pageSize: PAGE_SIZE })?.results || []);
  const [totalCount, setTotalCount] = useState(() => productService.peekProductsPage({ page: 1, pageSize: PAGE_SIZE })?.count || 0);
  const [page, setPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [laboratoires, setLaboratoires] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Filter selects live inside a collapsible « Filtres » panel (owner Studio).
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [selectedLaboratoire, setSelectedLaboratoire] = useState("");
  const [selectedStockState, setSelectedStockState] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  const STOCK_STATES = [
    { value: "in", label: "En stock" },
    { value: "low", label: "Stock bas" },
    { value: "out", label: "Rupture" },
  ];

  // Debounce the search box — refetch 300ms after typing stops.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever filters/search change.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedCategory, selectedSubcategory, selectedLaboratoire, selectedStockState]);

  // Fetch one page worth of products from the server.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const { results, count } = await productService.getProductsPage({
          page,
          pageSize: PAGE_SIZE,
          search: searchTerm,
          category: selectedCategory,
          subcategory: selectedSubcategory,
          laboratoire: selectedLaboratoire,
          stockState: selectedStockState,
        });
        if (cancelled) return;
        setProducts(results);
        setTotalCount(count);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, searchTerm, selectedCategory, selectedSubcategory, selectedLaboratoire, selectedStockState, reloadTick]);

  // Category + subcategory lookups (one-shot, full list).
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const data = await categoryService.getAllCategories();
        setCategories(Array.isArray(data) ? data : data?.results || []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
      try {
        const data = await subcategoryService.getAllSubcategories();
        setSubcategories(Array.isArray(data) ? data : data?.results || []);
      } catch (err) {
        console.error("Error fetching subcategories:", err);
      }
      try {
        const data = await laboratoireService.getAllLaboratoires();
        setLaboratoires(Array.isArray(data) ? data : data?.results || []);
      } catch (err) {
        console.error("Error fetching laboratoires:", err);
      }
    };
    loadLookups();
  }, []);

  const filteredSubcategories = useMemo(
    () =>
      selectedCategory
        ? subcategories.filter((sub) => {
            const categoryId =
              typeof sub.category === "object" ? sub.category.id : sub.category;
            return categoryId && categoryId.toString() === selectedCategory;
          })
        : [],
    [subcategories, selectedCategory]
  );

  const hasActiveFilters = Boolean(
    searchTerm || selectedCategory || selectedSubcategory || selectedLaboratoire || selectedStockState
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  // Actualiser = reset all filters/search back to default, then reload.
  const refresh = () => {
    setSearchInput("");
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedSubcategory("");
    setSelectedLaboratoire("");
    setSelectedStockState("");
    setPage(1);
    setReloadTick((n) => n + 1);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;
    try {
      await productService.deleteProduct(id);
      // Refetch current page so counts stay right.
      const { results, count } = await productService.getProductsPage({
        page,
        pageSize: PAGE_SIZE,
        search: searchTerm,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        laboratoire: selectedLaboratoire,
        stockState: selectedStockState,
      });
      setProducts(results);
      setTotalCount(count);
      // If this page is now empty and we were past page 1, step back.
      if (results.length === 0 && page > 1) setPage(page - 1);
      setSelectedProduct(null); // close the popup after a successful delete
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("Failed to delete product. Please try again.");
    }
  };

  const handleEditProduct = (id) => navigate(`/products/${id}/edit`);

  // Build a compact page list: 1 … p-1 p p+1 … N
  const pageButtons = useMemo(() => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:items-center">
        {/* Owner 2026-09-09 : ligne d'en-tête calée sur VenteTabs de « Nouvelle vente »
            — chip .tab-chip-on (42px) et mb-4 sous la ligne, pour que la barre de
            recherche et la liste tombent à la même hauteur que sur /seller-sales. */}
        {/* Page name as a gray chip — same design as the AddProduct page
            (owner Studio); icon box, title, refresh + Nouveau produit removed. */}
        <div className="flex items-center min-w-0 flex-1">
          {/* Chip = refresh button (owner Studio 2026-08-13; pages « ajouter » excluded) */}
          <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="tab-chip-on">
            <IcPackageOpen size={13} /> {appText('products_title', 'Gestion des produits')}
          </button>
        </div>
        {/* Promo + Catégories — same container/design as the Produits/Services
            toggle on the seller-sales page (owner Studio): Promo = « Produits »
            look, Catégories = « Services » look. */}
        <div className="inline-flex bg-gray-100 rounded-xl p-1">
          <Link
            to="/products/promos"
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 bg-gray-100 text-gray-500 shadow-sm border border-transparent"
          >
            <IcPercent size={13} /> Promo
          </Link>
          {/* Chip « Catégories » retiré → Paramètres > Catégories produits (owner 2026-09-08). */}
        </div>
      </div>

      {/* Toolbar: search + Filtres — same layout as the seller-sales page
          (owner Studio): full-width search (white border, dark focus ring),
          Filtres button at the right end of the line. */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            className="inp h-12 w-full pl-11 pr-10"
            placeholder="Rechercher par nom ou code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchInput("")}
            >
              <IcClose />
            </button>
          )}
        </div>

        {/* Filtres toggle — same button as on the seller-sales page (owner). */}
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
        >
          <IcFilters className="text-sm" /> Filtres
        </button>
      </div>

      {/* Filter panel: laboratoire / catégorie / sous-catégorie / disponibilité
          — carte de filtres Composants-2 §3 : libellé 11px gris au-dessus de
          chaque select 38px bordé #D1D5DB (chevron gris). */}
      {showFilters && (
      <div className="flt-card rounded-2xl p-4 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

        {/* 1) Laboratoire */}
        <div>
          <label className="flt-label" htmlFor="pl-filter-lab">Laboratoire</label>
          <div className="flt-sel">
            <select
              id="pl-filter-lab"
              className="flt-select pl-4 py-2.5 rounded-xl text-sm"
              value={selectedLaboratoire}
              onChange={(e) => setSelectedLaboratoire(e.target.value)}
            >
              <option value="">Tous</option>
              {laboratoires.map((lab) => (
                <option key={lab.id} value={lab.id.toString()}>
                  {lab.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 2) Catégorie */}
        <div>
          <label className="flt-label" htmlFor="pl-filter-cat">Catégorie</label>
          <div className="flt-sel">
            <select
              id="pl-filter-cat"
              className="flt-select pl-4 py-2.5 rounded-xl text-sm"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedSubcategory("");
              }}
            >
              <option value="">Toutes</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id.toString()}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 3) Sous-catégorie */}
        <div>
          <label className="flt-label" htmlFor="pl-filter-subcat">Sous-catégorie</label>
          <div className="flt-sel">
            <select
              id="pl-filter-subcat"
              className="flt-select pl-4 py-2.5 rounded-xl text-sm"
              value={selectedSubcategory}
              onChange={(e) => setSelectedSubcategory(e.target.value)}
              disabled={!selectedCategory}
            >
              <option value="">Toutes</option>
              {filteredSubcategories.map((subcategory) => (
                <option key={subcategory.id} value={subcategory.id.toString()}>
                  {subcategory.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 4) Disponibilité (état du stock) */}
        <div>
          <label className="flt-label" htmlFor="pl-filter-stock">Disponibilité</label>
          <div className="flt-sel">
            <select
              id="pl-filter-stock"
              aria-label="Disponibilité"
              className="flt-select pl-4 py-2.5 rounded-xl text-sm"
              value={selectedStockState}
              onChange={(e) => setSelectedStockState(e.target.value)}
            >
              <option value="">Toutes</option>
              {STOCK_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && products.length === 0 ? (
        /* Squelette (index.css §12, owner 2026-09-10) : la carte à la forme
           exacte du tableau (entête, 10 lignes de 81 px, pied) — la page
           garde ses 1235 px pendant le chargement, plus d'anneau bleu. */
        <TableSkeleton
          columns={[
            { label: 'Produit', avatar: true, w: 300 },
            { label: 'Catégorie', w: 64 },
            { label: 'Sous-catégorie', w: 96 },
            { label: 'Prix', align: 'right', w: 72 },
            { label: 'Stock', align: 'right', w: 84, h: 22 },
            { label: '' },
          ]}
        />
      ) : products.length === 0 ? (
        /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase
           13px 600, sous-phrase 11.5px, action encre 36px */
        <div className="empty-ki rounded-2xl p-12">
          <IcPackageOpen size={48} className="empty-ki-icon w-12 h-12 mb-4" />
          <p className="empty-ki-title text-lg mt-0">
            {hasActiveFilters
              ? "Aucun produit correspondant aux critères"
              : "Aucun produit trouvé"}
          </p>
          <p className="empty-ki-sub text-sm mt-1 mb-0 max-w-md mx-auto">
            {hasActiveFilters
              ? "Essayez de modifier vos filtres ou votre recherche."
              : "Pour commencer, ajoutez votre premier produit en cliquant sur 'Ajouter un produit'."}
          </p>
          {!hasActiveFilters && (
            <Link
              to="/products/add"
              className="btn-add-block w-auto mt-5 px-4 py-2.5 transition-colors"
            >
              <IcPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              Ajouter un produit
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            {/* Tableau dense (Composants-2 §6) : en-tête th-ki #e9e9ea 8/12px,
                lignes 13px 8/12px, nom 600, prix à droite tabulaire 600,
                badge stock dans la cellule, survol #F0FDF4, chevron ›. */}
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-ki px-5 py-3 text-xs text-left uppercase">Produit</th>
                  <th className="th-ki px-5 py-3 text-xs text-left uppercase">Catégorie</th>
                  <th className="th-ki px-5 py-3 text-xs text-left uppercase">Sous-catégorie</th>
                  <th className="th-ki px-5 py-3 text-xs text-right uppercase">Prix</th>
                  <th className="th-ki px-5 py-3 text-xs text-right uppercase">Stock</th>
                  <th className="th-ki px-5 py-3 w-8" aria-label="Ouvrir" />
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const stockVal = typeof product.stock === "number" ? product.stock : 0;
                  const isOut = stockVal <= 0;
                  const isLow = !isOut && stockVal <= product.stock_min;
                  /* Livre §05 « Tableau dense » : cellule STOCK = « N En Stock »
                     teinté vert ; stock bas = teinte rouge « N Stock bas ». Le
                     séparateur « · » a été retiré et le nombre seul libellé
                     « En Stock » le 2026-09-07 (owner).
                     « Rupture » = aplat #DC2626 (bdg-rupture, §04) depuis le
                     2026-09-07 (owner) — la teinte claire ne se voyait pas assez
                     et la modale de détail de cette même page l'affichait déjà
                     en aplat. */
                  const stockPill = isOut
                    ? "bdg-rupture"
                    : isLow
                    ? "bdg-stock-low"
                    : "bdg-stock-ok";
                  const initials = (product.name || "?")
                    .trim()
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase();
                  return (
                    <tr
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      className="border-t border-gray-100 tr-ki transition-colors"
                    >
                      <td className="td-ki px-5 py-4 text-sm">
                        <div className="flex items-center gap-3">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="h-12 w-12 rounded-xl object-contain bg-gray-50 border border-gray-100 p-0.5"
                            />
                          ) : (
                            <span className="h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                              {initials || <IcPackageOpen />}
                            </span>
                          )}
                          <span className="font-semibold text-gray-900">{product.name}</span>
                        </div>
                      </td>
                      <td className="td-ki px-5 py-4 text-sm">
                        {product.category ? (
                          <span className="bdg bdg-cat">
                            {product.category.name || "Sans catégorie"}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="td-ki px-5 py-4 text-sm">
                        {product.subcategory ? (
                          <span className="bdg bdg-subcat">
                            {typeof product.subcategory === "object"
                              ? product.subcategory.name
                              : product.subcategory}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="td-ki px-5 py-4 text-sm text-right tabular-nums whitespace-nowrap">
                        {product.price ? (
                          <span className="font-semibold text-gray-900">{Number(product.price).toFixed(3)} DT</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="td-ki px-5 py-4 text-sm text-right">
                        <span className={`bdg bdg-td text-xs px-2.5 py-[3px] ${stockPill}`}>
                          {isOut ? 'Rupture' : isLow ? `${stockVal} Stock bas` : `${stockVal} En Stock`}
                        </span>
                      </td>
                      <td className="td-ki px-5 py-4 text-sm text-right text-gray-400">
                        <IcChevronRight size={14} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          {/* Composants-2 §4 : compte total à gauche (12.5px gris tabulaire,
              flex-1), pages en carrés 34px, dernière page largeur auto. */}
          <div className="flex flex-col sm:flex-row items-center px-5 py-4 border-t border-gray-100 bg-white gap-3">
            <div className="text-[12.5px] text-gray-500 tabular-nums flex-1 w-full sm:w-auto">
              {totalCount === 0 ? "0 produit" : `${rangeStart}–${rangeEnd} sur ${totalCount} produits`}
            </div>
            <div className="inline-flex items-center gap-1">
              <button
                className="btn-day px-3 py-2 text-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                aria-label="Page précédente"
              >
                <IcChevronLeft className="w-3 h-3" />
              </button>
              {pageButtons.map((p, idx) => {
                const prev = pageButtons[idx - 1];
                const gap = prev && p - prev > 1;
                const wide = p === totalPages && p > 1;
                return (
                  <React.Fragment key={p}>
                    {gap && <span className="pg-dots w-auto h-auto px-2">…</span>}
                    <button
                      className={`${p === page ? "pg-on" : "pg-btn"}${wide ? " w-auto px-2" : ""}`}
                      onClick={() => setPage(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button
                className="btn-day px-3 py-2 text-sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                aria-label="Page suivante"
              >
                <IcChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
        />
      )}
    </div>
  );
};

export default ProductList;
